/* eslint-disable import/no-named-as-default-member */
import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Grid, IconButton, Box, Tabs, Tab, Button, alpha, Menu, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import SwipeableViews from 'react-swipeable-views';
import { AddOutlined, FileDownloadDoneOutlined, ModeEditOutlineOutlined, ClearAll, Sort } from '@mui/icons-material';
import Iconify from '../../../../components/Iconify';
import useTrade from '../../../../hooks/useTrade';
import Page from '../../../../components/Page';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useAuth from '../../../../hooks/useAuth';
// eslint-disable-next-line import/no-named-as-default
import WatchListItem from './WatchListItem';
import StaticWatchListItem from './StaticWatchListItem';
import { dispatch } from '../../../../redux/store';
import {
  addToWatchListSuccess,
  getUserPortfolio,
  getWatchList,
  getWatchlistSuccess,
} from '../../../../redux/slices/trades';

const LOCAL_STORAGE_KEY = 'selectedTab'; // Define a key to store the selected tab
const SORT_STORAGE_KEY = 'watchlistSort'; // Define a key to store sorting preference

const WatchlistRow = () => {
  const [searchText, setSearchText] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortType, setSortType] = useState(() => {
    const savedSort = localStorage.getItem(SORT_STORAGE_KEY);
    return savedSort || 'default';
  });
  const { user } = useAuth();
  const segments = ['MCX', 'NSE', 'COMEX', 'CRYTPO', 'FOREX'];
  const userSegments = user ? segments.filter((segment) => user[segment]?.allow) : [];

  // Get initial tab from local storage or default to the first segment
  const getInitialTab = () => {
    const storedTab = localStorage.getItem(LOCAL_STORAGE_KEY);
    return storedTab && userSegments.includes(storedTab) ? storedTab : userSegments[0];
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());
  const [showDelete, setShowDelete] = useState(false);
  const watchlist = useSelector((state) => state?.trade?.watchlist);
  const userTrades = useSelector((state) => state?.trade?.userTrades);
  const { localSocket } = useTrade();
  const navigate = useNavigate();

  // Fetch watchlist data from local storage or API
  useEffect(() => {
    dispatch(getWatchList());
    if (user.role.rolename === 'client') {
      dispatch(getUserPortfolio());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selectedTab to local storage whenever it changes
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    if (userTrades?.OPEN && selectedTab) {
      const openTradesSymbols = userTrades.OPEN.filter((trade) => trade.script.exchange.name === selectedTab);
      const currentWatchlistScripts = watchlist && watchlist[selectedTab] ? Object.values(watchlist[selectedTab]) : [];
      const newScripts = openTradesSymbols
        .filter(
          (trade) => !currentWatchlistScripts.some((script) => script.tradingsymbol === trade.script.tradingsymbol)
        )
        .map((trade) => trade.script);

      if (newScripts.length > 0) {
        const updatedWatchlist = {
          ...watchlist,
          [selectedTab]: [...currentWatchlistScripts, ...newScripts],
        };
        const isUpdated = JSON.stringify(currentWatchlistScripts) !== JSON.stringify(updatedWatchlist[selectedTab]);

        if (isUpdated) {
          dispatch(getWatchlistSuccess(updatedWatchlist));
          localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
        }
      }
    }
  }, [userTrades?.OPEN, selectedTab, watchlist]);

  useEffect(() => {
    const tokenArray =
      watchlist && watchlist[selectedTab]
        ? Object.values(watchlist[selectedTab]).map((script) => +script.instrument_token)
        : [];

    if (tokenArray.length) {
      const payload = tokenArray.map((token) => +token);

      const sendSubscribeMessage = () => {
        localSocket.send(JSON.stringify({ event: 'subscribe', data: payload }));
      };

      if (localSocket) {
        if (localSocket.readyState === 1) {
          setTimeout(() => {
            sendSubscribeMessage();
          }, 100);
        }
        localSocket.onopen = () => {
          setTimeout(() => {
            sendSubscribeMessage();
          }, 100);
        };
      }
    }

    return () => {
      if (localSocket && localSocket.readyState === WebSocket.OPEN) {
        if (tokenArray.length > 0) {
          localSocket.send(JSON.stringify({ event: 'unsubscribe', data: tokenArray.map((token) => +token) }));
        }
      }
    };
  }, [watchlist, selectedTab, localSocket?.readyState, localSocket]);

  useEffect(() => {
    const currentDate = new Date();

    const removeExpiredScripts = () => {
      if (watchlist && selectedTab && watchlist[selectedTab]) {
        const updatedWatchlist = { ...watchlist };

        updatedWatchlist[selectedTab] = updatedWatchlist[selectedTab].filter((script) => {
          const expiryDate = new Date(script.squareoff_date);
          return expiryDate >= currentDate;
        });

        // Check if any scripts were removed
        if (updatedWatchlist[selectedTab].length !== watchlist[selectedTab].length) {
          localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
          dispatch(getWatchlistSuccess(updatedWatchlist));
        }
      }
    };

    removeExpiredScripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist, selectedTab, dispatch]);

  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, sortType);
  }, [sortType]);

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (type) => {
    setSortType(type);
    handleSortClose();
  };

  const getSortedItems = (items) => {
    switch (sortType) {
      case 'atoz':
        return [...items].sort((a, b) => a.name.localeCompare(b.name));
      case 'netchange':
        return [...items].sort((a, b) => (b.change || 0) - (a.change || 0));
      default:
        return items;
    }
  };

  const filteredItems =
    watchlist && watchlist[selectedTab]
      ? getSortedItems(
        Object.values(watchlist[selectedTab])
          .filter((script) => script.name.toLowerCase().includes(searchText.toLowerCase()))
      )
      : [];

  // Get header margin from localStorage (for custom positioning if needed)
  const getHeaderMargin = () => {
    const savedMargin = localStorage.getItem('watchlistHeaderMargin');
    return savedMargin ? Number(savedMargin) : -3; // Default to -3 (removes top gap)
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleChangeTab = (event, newTab) => {
    setSelectedTab(newTab);
  };

  const handleWatchListClick = () => {
    if (user.role.rolename === 'client') {
      navigate(PATH_DASHBOARD.bankerRole.watchlist.add);
    }
    if (user.role.rolename === 'admin') {
      navigate(PATH_DASHBOARD.insurerRole.watchlist.add);
    }
    if (user.role.rolename === 'broker') {
      navigate(PATH_DASHBOARD.operatorRole.watchlist.add);
    }
    if (user.role.rolename === 'superAdmin') {
      navigate(PATH_DASHBOARD.watchlist.add);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const items = [...filteredItems];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedWatchlist = { ...watchlist, [selectedTab]: items };
    localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
    dispatch(getWatchlistSuccess(updatedWatchlist));
  };

  const handleDeleteItem = useCallback(
    (itemId) => {
      const updatedWatchlist = { ...watchlist };
      updatedWatchlist[selectedTab] = updatedWatchlist[selectedTab].filter((item) => item._id !== itemId);
      localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
      dispatch(addToWatchListSuccess(updatedWatchlist));
    },
    [selectedTab, watchlist]
  );

  const handleEditChange = () => {
    setShowDelete((prev) => !prev);
  };

  return (
    <Page title={'Watchlist'} sx={{ px: 0.5, py: 0 }}>
      <Grid container spacing={0}>
        {user.role.rolename === 'client' ? (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1100,
              backgroundColor: 'background.paper',
              width: '100%',
              borderRadius: 1,
              overflow: 'hidden',
              boxShadow: (theme) => `0 0 2px 0 ${alpha(theme.palette.grey[500], 0.2)}, 
                                 0 12px 24px -4px ${alpha(theme.palette.grey[500], 0.12)}`,
              mt: getHeaderMargin() / 8, // Use custom header margin from localStorage (default: -3 converted to theme units)
            }}
          >
            <Grid container spacing={0} sx={{
              color: 'text.primary',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                py: 0.75,
                px: 1,
                bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.08),
                backdropFilter: 'blur(8px)',
                height: 44,
              }}
              >
                {!showDelete && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      sx={{
                        p: 0.5,
                        color: 'primary.main',
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s',
                      }}
                      onClick={handleWatchListClick}
                    >
                      <AddOutlined sx={{ fontSize: 24 }} />
                    </IconButton>
                  </Box>
                )}

                {/* Centered Watchlist Heading using Box */}
                {watchlist && !!Object.keys(watchlist).length && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 1
                  }}>
                    {/* Left side - ClearAll icon */}
                    <Box sx={{
                      visibility: showDelete ? 'visible' : 'hidden',
                      width: '32px',
                      transition: 'all 0.2s'
                    }}>
                      {showDelete && (
                        <IconButton
                          sx={{
                            p: 0.5,
                            color: 'error.main',
                            '&:hover': {
                              bgcolor: 'error.lighter',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s',
                          }}
                          onClick={() => {
                            const updatedWatchlist = { ...watchlist };
                            updatedWatchlist[selectedTab] = [];
                            localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
                            dispatch(getWatchlistSuccess(updatedWatchlist));
                          }}
                          title="Remove All Scripts"
                        >
                          <ClearAll sx={{ fontSize: 24 }} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Center - WATCHLIST text with profile icon */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexGrow: 1,
                      gap: 1,
                    }}>
                      <Box sx={{
                        typography: 'subtitle1',
                        fontWeight: 600,
                        color: 'text.primary',
                        letterSpacing: 0.5,
                      }}>
                        WATCHLIST
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => navigate('/client/profile')}
                        sx={{
                          p: 0.5,
                          width: 28,
                          height: 28,
                          color: 'primary.main',
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                          border: '1px solid',
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                            borderColor: 'primary.main',
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                        title="Go to Profile"
                      >
                        <Iconify
                          icon="eva:person-fill"
                          sx={{
                            fontSize: 14,
                          }}
                        />
                      </IconButton>
                    </Box>

                    {/* Right side - Edit/Done icon */}
                    <Box>
                      <IconButton
                        sx={{
                          p: 0.5,
                          color: 'primary.main',
                          '&:hover': {
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s',
                        }}
                        onClick={handleEditChange}
                      >
                        {showDelete ? (
                          <FileDownloadDoneOutlined sx={{ fontSize: 24 }} />
                        ) : (
                          <ModeEditOutlineOutlined sx={{ fontSize: 24 }} />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Tabs for user segments */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    borderRadius: '8px',
                    mx: 1,
                    mb: 0.5,
                    p: 0.25,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    backgroundColor: (theme) => alpha(theme.palette.background.neutral, 0.5),
                  }}
                >
                  <Tabs
                    value={selectedTab}
                    onChange={handleChangeTab}
                    variant="fullWidth"
                    scrollButtons={false}
                    aria-label="watchlist tabs"
                    TabIndicatorProps={{
                      style: { display: 'none' }
                    }}
                    sx={{
                      minHeight: 34,
                      width: '100%',
                      '& .MuiTabs-flexContainer': {
                        width: '100%',
                        display: 'flex',
                      }
                    }}
                  >
                    {userSegments.map((exchange, index) => {
                      // Calculate z-index to ensure selected tab appears above others
                      const zIndex = selectedTab === exchange ? 2 : 1;

                      return (
                        <Tab
                          label={exchange}
                          value={exchange}
                          key={exchange}
                          disableRipple
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            px: 1.5,
                            py: 0.75,
                            minHeight: '34px',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            zIndex,
                            color: selectedTab === exchange ? 'primary.main' : 'text.secondary',
                            backgroundColor: selectedTab === exchange
                              ? (theme) => alpha(theme.palette.background.paper, 1)
                              : 'transparent',
                            position: 'relative',
                            borderRadius: (() => {
                              if (index === 0) return '8px 0 0 8px';
                              if (index === userSegments.length - 1) return '0 8px 8px 0';
                              return 0;
                            })(),
                            border: '1px solid',
                            borderColor: selectedTab === exchange
                              ? (theme) => alpha(theme.palette.primary.main, 0.25)
                              : 'transparent',
                            boxShadow: selectedTab === exchange
                              ? (theme) => `0 1px 3px ${alpha(theme.palette.primary.main, 0.08)}`
                              : 'none',
                            transition: 'all 0.3s',
                            '&.Mui-selected': {
                              color: 'primary.main',
                              fontWeight: 700,
                            },
                            '&:hover': {
                              backgroundColor: selectedTab === exchange
                                ? (theme) => alpha(theme.palette.background.paper, 1)
                                : (theme) => alpha(theme.palette.background.default, 0.8),
                              color: selectedTab === exchange ? 'primary.main' : 'primary.dark',
                            }
                          }}
                        />
                      );
                    })}
                  </Tabs>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ px: 1, py: 0.75 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={handleSortClick}
                      sx={{
                        height: 32,
                        width: 32,
                        color: sortType !== 'default' ? 'primary.main' : 'text.disabled',
                        backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.04),
                        '&:hover': {
                          backgroundColor: 'background.paper',
                        },
                      }}
                    >
                      <Sort />
                    </IconButton>

                    <TextField
                      fullWidth
                      placeholder="Search Symbols"
                      value={searchText}
                      onChange={handleSearchChange}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <Iconify
                            icon="eva:search-fill"
                            sx={{
                              color: 'text.disabled',
                              width: 16,
                              height: 16,
                              mr: 0.5
                            }}
                          />
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.04),
                          fontSize: '0.875rem',
                          transition: 'all 0.2s',
                          '& fieldset': {
                            borderColor: (theme) => alpha(theme.palette.grey[500], 0.16),
                            borderWidth: 1,
                          },
                          '&:hover': {
                            backgroundColor: 'background.paper',
                            '& fieldset': {
                              borderColor: 'primary.lighter',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'background.paper',
                            boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`,
                            '& fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: 1,
                            },
                          },
                        },
                        '& .MuiInputBase-input': {
                          '&::placeholder': {
                            color: 'text.disabled',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Box>
                  <Menu
                    anchorEl={sortAnchorEl}
                    open={Boolean(sortAnchorEl)}
                    onClose={handleSortClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <MenuItem
                      onClick={() => handleSortSelect('default')}
                      sx={{
                        color: sortType === 'default' ? 'primary.main' : 'text.primary',
                        fontWeight: sortType === 'default' ? 600 : 400,
                      }}
                    >
                      Default Order
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleSortSelect('atoz')}
                      sx={{
                        color: sortType === 'atoz' ? 'primary.main' : 'text.primary',
                        fontWeight: sortType === 'atoz' ? 600 : 400,
                      }}
                    >
                      Sort A to Z
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleSortSelect('netchange')}
                      sx={{
                        color: sortType === 'netchange' ? 'primary.main' : 'text.primary',
                        fontWeight: sortType === 'netchange' ? 600 : 400,
                      }}
                    >
                      Sort by Net Change
                    </MenuItem>
                  </Menu>
                </Box>
              </Grid>
            </Grid>
          </Box>) : (
          <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, backgroundColor: 'background.paper', width: '100%' }}>
            <Grid container spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  pt: 2,
                }}
              >
                {!showDelete && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton color="primary" sx={{ padding: 0, mx: 1 }} onClick={handleWatchListClick}>
                      <Iconify icon={'eva:plus-circle-outline'} sx={{ fontSize: '40px' }} />
                    </IconButton>
                  </Box>
                )}

                {watchlist && !!Object.keys(watchlist).length && (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                    <Button variant="contained" onClick={handleEditChange}>
                      {showDelete ? 'DONE' : 'EDIT'}
                    </Button>
                  </Box>
                )}
              </Box>
              <Grid item xs={12}>
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: '8px',
                    mx: 1,
                    mb: 0.5,
                    overflow: 'hidden',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                  }}
                >
                  <Tabs
                    value={selectedTab}
                    onChange={handleChangeTab}
                    variant="scrollable"
                    scrollButtons={false}
                    aria-label="watchlist tabs"
                    TabIndicatorProps={{
                      style: { display: 'none' }
                    }}
                    sx={{
                      minHeight: 40,
                      width: '100%',
                      '& .MuiTabs-flexContainer': {
                        width: '100%',
                        display: 'flex',
                      },
                      '& .MuiTabs-scroller': {
                        width: '100%',
                      },
                    }}
                  >
                    {userSegments.map((exchange, index) => {
                      const tabWidth = 100 / userSegments.length;
                      return (
                        <Tab
                          label={exchange}
                          value={exchange}
                          key={exchange}
                          disableRipple
                          sx={{
                            width: `${tabWidth}%`,
                            maxWidth: 'none',
                            minWidth: 0,
                            flex: 1,
                            px: 0,
                            py: 1,
                            m: 0,
                            minHeight: '40px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            color: 'text.secondary',
                            background: 'background.paper',
                            transition: 'all 0.15s',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            borderRight: index !== userSegments.length - 1 ? '1px solid' : 'none',
                            borderRightColor: 'divider',
                            borderRadius: 0,
                            '&.Mui-selected': {
                              color: 'primary.main',
                              fontWeight: 600,
                              borderBottom: '2px solid',
                              borderColor: 'primary.main',
                              background: 'transparent',
                            },
                            '&:hover': {
                              color: 'primary.main',
                            },
                          }}
                        />
                      );
                    })}
                  </Tabs>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Search Symbols" value={searchText} onChange={handleSearchChange} />
              </Grid>
            </Grid>
          </Box>)}
        <Grid item xs={12}>
          <SwipeableViews
            index={userSegments.indexOf(selectedTab)}
            onChangeIndex={(index) => setSelectedTab(userSegments[index])}
          >
            {userSegments.map((exchange) => (
              <Box key={exchange}>
                {showDelete ? (
                  <WatchListItem
                    filteredItems={filteredItems}
                    handleDeleteItem={handleDeleteItem}
                    handleDragEnd={handleDragEnd}
                    showDelete={showDelete}
                    user={user}
                    selectedTab={selectedTab}
                  />
                ) : (
                  <StaticWatchListItem
                    filteredItems={filteredItems}
                    handleDeleteItem={handleDeleteItem}
                    handleDragEnd={handleDragEnd}
                    showDelete={showDelete}
                    user={user}
                  />
                )}
              </Box>
            ))}
          </SwipeableViews>
        </Grid>
      </Grid>
    </Page>
  );
};

export default WatchlistRow;
